import { generateApiPath, writeItem } from '#app/Handler/api';
import useDeposit from '#app/View/InventoryManagement/Inventory/DepositInventory/hooks/useDeposit';
import { Filters } from '#app/View/InventoryManagement/Inventory/DepositInventory/hooks/useInventory';
import DepositEntryPoints from '#app/entrypoints/deposit';
import { useCollectionPaginated } from '#app/hooks/useCollectionPaginated';
import { InventoryInfo } from '#app/typings/deposit';

type HookReturn = {
  update: (row: InventoryInfo) => Promise<InventoryInfo>;
  cancel: (row: InventoryInfo) => Promise<InventoryInfo>;
};

const useActions = (
  mutate: ReturnType<typeof useCollectionPaginated<InventoryInfo, Filters>>['mutate'],
): HookReturn => {
  const { deposit } = useDeposit();

  const update = async (row: InventoryInfo): Promise<InventoryInfo> => {
    await writeItem('PUT')(
      generateApiPath(DepositEntryPoints.inventoryUpdate, { deposit: deposit as string, id: row.id }),
      { arg: { storageLocation: row.storageLocation, quantity: row.quantity } },
    );

    mutate(
      (current) => {
        if (!current) {
          return current;
        }

        return {
          ...current,
          data: current.data.map((item) => (item.id === row.id ? { ...item, ...row } : item)),
        };
      },
      { revalidate: false },
    );

    return row;
  };

  const cancel = async (row: InventoryInfo): Promise<InventoryInfo> => {
    await writeItem('DELETE')(
      generateApiPath(DepositEntryPoints.inventoryUpdate, { deposit: deposit as string, id: row.id }),
      {},
    );

    mutate(
      (current) => {
        if (!current) {
          return current;
        }

        return {
          ...current,
          data: current.data.map((item) => (item.id === row.id ? { ...item, quantity: null } : item)),
        };
      },
      { revalidate: false },
    );

    return row;
  };

  return {
    update,
    cancel,
  };
};

export default useActions;
